import create from 'zustand';
import { GetState, SetState } from 'types/zustand';
import {
  AccountPopupNotificationSetting,
  DoNotShowNotificationType,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Accounts';
import {
  getPopupNotificationSettings,
  savePopupNotificationSettings,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Accounts/AccountService';
import { NotificationMessage } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Notifications';
import produce from 'immer';
import { generateGuid } from 'utils/Guid';
import { getCookie } from 'utils/ApiUtils';
import { getPortalContentScrollY, usePortalStore } from './PortalStore';
import { getNotificationTimeout, useSettingsStore } from './SettingsStore';

// this is just in the module, because we don't need this to trigger renders.
let notificationTimer: number;

export interface INotificationAddedEvent {
  origin: any; //NotificationManager;
  notification: any; //NotificationItem;
  addSilently?: boolean;
}

export interface INotificationManagerHandlersProps {
  stickTo: HTMLElement;
  parent: HTMLElement;
  onClose?: () => void;
  onShow?: () => void;
  onNotificationAdded: (event: INotificationAddedEvent) => void;
  notificationSettings: AccountPopupNotificationSetting;
}

//#region store creation

const defaultNotificationState = {
  buttonElement: null as HTMLElement | null,
  popupSettings: null as AccountPopupNotificationSetting | null,
  notificationReceived: false,
  notificationShowing: false,
  /** Map will store in order received. */
  notifications: new Map<string, NotificationMessage>(),
  page: 0,
};

type INotificationState = typeof defaultNotificationState;

// factory for actions object so we don't need TS interface to describe them
function createActions(get: GetState<INotificationStore>, set: SetState<INotificationState>) {
  return {
    loadSettings: async () => {
      const { actions, buttonElement } = get();
      const popupSettings = await getPopupNotificationSettings();
      set({ popupSettings });
      if (buttonElement) {
        actions.initialize(buttonElement, popupSettings);
      }
    },

    initialize: (buttonElement: HTMLElement, settings: AccountPopupNotificationSetting) => {
      const { actions } = get();
      const { onNotificationAdded } = actions;
      const props: INotificationManagerHandlersProps = {
        stickTo: buttonElement,
        // parent: document.getElementsByClassName('dp-portal-frame__page-contents')[0] as HTMLElement,
        parent: document.getElementsByClassName('dp-portal-frame__contents')[0] as HTMLElement,
        // onClose,
        // onShow,
        onNotificationAdded,
        notificationSettings: settings,
      };
      if (!DEV_SERVER && !IS_TEST) {
        $DP.UI.Controls.Notification.NotificationManager.instance.initialize(props);
        // $DP.Portal.PortalPage.initializeNotificationsStatic(props);
      }

      // subscribe to portal scroll changes:
      usePortalStore.subscribe((state, prevState) => {
        // won't need this when we can delegate to React / use hooks, but for now:
        if (getPortalContentScrollY(state) !== getPortalContentScrollY(prevState)) {
          get().actions.onParentScroll();
        }
      });
    },

    onElementReady: (buttonElement: HTMLElement | null) => {
      const { actions, popupSettings, buttonElement: currentButton } = get();
      if (!buttonElement || currentButton) {
        // this should only be set one time.
        return;
      }
      set({ buttonElement });
      if (popupSettings?.AccountId) {
        // settings set before element rendered. Initialize now.
        actions.initialize(buttonElement, popupSettings);
      }
    },

    hideNotificationBox: () => {
      if (!DEV_SERVER && !IS_TEST) {
        $DP.UI.Controls.Notification.NotificationManager.instance.hideNotificationBox();
      }
      get().actions.onHide();
    },

    onHide() {
      if (get().notificationShowing === false) {
        // since notifications can come as a blitz, don't spam subscribers if we get another update to the same value
        return;
      }
      set({ notificationShowing: false });
    },

    showNotificationBox: () => {
      if (!DEV_SERVER && !IS_TEST) {
        $DP.UI.Controls.Notification.NotificationManager.instance.showNotificationBox();
      }

      get().actions.onShow();
      // get().actions.resumeTimer(); TODO when we replace NotificationManager
    },

    onParentScroll: () => {
      if (!DEV_SERVER && !IS_TEST) {
        $DP.UI.Controls.Notification.NotificationManager.instance.setPositionForNotificationBox(
          get().notificationShowing
        );
      }
      // later we can just calculate in the component
    },

    onShow() {
      if (get().notificationShowing) {
        // since notifications can come as a blitz, don't spam subscribers if we get another update to the same value
        return;
      }
      // show callout, but also restore "newly received" icon state to false:
      set({ notificationShowing: true, notificationReceived: false });
    },

    toggleNotifications: () => {
      const { actions, notificationShowing } = get();
      if (notificationShowing) {
        actions.hideNotificationBox();
      } else {
        actions.showNotificationBox();
      }
    },
    addNotification(_eventId: string, message: NotificationMessage, addSilently = false) {
      const state = get();
      const { actions } = state;
      // just going to store it:
      set(
        produce<INotificationState>(state => {
          // eventId is something about the type of notification, and not unique
          state.notifications.set(generateGuid(), message);
        })
      );
      if (!addSilently) {
        if (getShouldShowNotification(state)) {
          // there was a bunch of setTimeout stuff here in legacy that was essentially a 500ms debounce,
          // but we shouldn't need it here
          actions.showNotificationBox();
          this.resumeTimer();
        } else {
          actions.hideNotificationBox();
        }
      }
    },
    pauseTimer() {
      if (notificationTimer) {
        window.clearInterval(notificationTimer);
      }
      notificationTimer = 0;
    },
    resumeTimer() {
      // the whole box is hidden on a timer:
      const interval = getNotificationTimeout(useSettingsStore.getState());
      if (notificationTimer) {
        window.clearInterval(notificationTimer);
      }
      notificationTimer = window.setTimeout(() => {
        get().actions.hideNotificationBox();
      }, interval);
    },
    onNotificationAdded: ({ origin, addSilently }: INotificationAddedEvent) => {
      if (origin?.shouldShowNotification && addSilently && !origin?.isVisible) {
        get().actions.indicateNotificationReceived();
      }
    },
    indicateNotificationReceived: () => {
      set({ notificationReceived: true });
    },
    pageForward() {},
    pageBack() {},
    /**
     * update notification setting locally, and also push to server
     * @param value to set
     */
    updateNotificationSetting(value: DoNotShowNotificationType) {
      set(
        produce<INotificationState>(state => {
          // eventId is something about the type of notification, and not unique
          if (state.popupSettings) {
            // legacy re-assigned sessionId here, but how could it get out of date?
            state.popupSettings.AccountId = getCookie('DecisionsSessionID');
            state.popupSettings.DoNotShowNotificationType = value;
          }
          savePopupNotificationSettings({
            doNotShowNotificationType: value,
          });
        })
      );
    },
  };
}

// instead of manual TS interface for the whole store, we can merge types with actions and state
type INotificationStore = INotificationState & {
  actions: ReturnType<typeof createActions>;
};

/**
 * if you will have multiple instances of your store, a factory function will DRY creation up for you
 *
 * If you need state to be global / singleton, then just create it immediately and export / import it
 * (per standard zustand use-case.)
 */
export const useNotificationStore = create<INotificationStore>((set, get) => ({
  ...defaultNotificationState,
  actions: createActions(get, set),
}));

//#endregion

//#region selector functions

// selector functions could just as easily be inline, but having them pre-defined can DRY up
// reuse, and make them easier to test if they had a complex calculation.
// They also shield components from the effects of store-object refactors.

export function getNotificationPopupSettings(state: INotificationStore) {
  return state.popupSettings;
}

export function getNotificationActions(state: INotificationStore) {
  return state.actions;
}

export function getNotificationReceived(state: INotificationStore) {
  return state.notificationReceived;
}

export function getNotificationButton(state: INotificationStore) {
  return state.buttonElement;
}

export function getNotificationShowing(state: INotificationStore) {
  return state.notificationShowing;
}

export function getNotificationMessages(state: INotificationStore) {
  return state.notifications;
}
export function getNotificationPage(state: INotificationStore) {
  return state.page;
}

export function getShouldShowNotification(state: INotificationStore) {
  const setting = getNotificationPopupSettings(state);
  if (!setting) {
    return;
  }

  switch (setting.DoNotShowNotificationType) {
    case DoNotShowNotificationType.AutoShow:
      return true;
    case DoNotShowNotificationType.Ever:
      return false;
    case DoNotShowNotificationType.CurrentSession:
      return setting.SessionId !== getCookie('DecisionsSessionID');
    case DoNotShowNotificationType.Today:
      return new Date(setting.ExpireDate) < new Date();
    default:
      return true;
  }
}

//#endregion

//#region hooks
// hooks help DRY up boilerplate around getting certain references, e.g.

/**
 * Notification of a selector hook that uses local state and memoizes with useCallback
 * @param path some local state you need to use with shared state to pick / derive a value
 * @returns that unique value
 */
// export function useSelectorWithLocalState(path: string) {
//   return useNotificationStore(
//     useCallback((state) => (state.nestedState as any)[path], [path])
//   );
// }

//#endregion hooks
